import { useSignal } from '@preact/signals';
import { useEffect } from 'preact/hooks';
import { useLocation } from 'preact-iso';

import { Button, Checkbox, Notice, TextField } from '@amumin/ui';

import { api, errorMessage } from '../api.js';
import { loginDestination } from '../lib/pairingResume.js';
import { AuthLayout } from '../components/AuthLayout.js';
import { normalizeEmail, validateLogin } from '../lib/validation.js';
import {
  appText,
  authState,
  loginHint,
  messages,
  refreshContext,
  setAuthenticated,
  setPendingChallenge,
  sharedComputer,
} from '../state/session.js';

/**
 * Wachtwoordscherm (ONTWERP.md §11.1.1): vinkje "Gedeelde of openbare computer" op elk
 * wachtwoordscherm, "Ingelogd blijven" (default aan; server past `remember_default` toe),
 * uitleg bij `reason=untrusted_context` (regel 7).
 */
export function LoginPage() {
  const { query, route } = useLocation();
  const m = messages.value;
  // Review golf 8 r2: na "uitnodiging aangenomen" staat het adres al ingevuld.
  const hint = loginHint.peek();
  const email = useSignal(hint?.email ?? '');
  const password = useSignal('');
  const shared = useSignal(false);
  const remember = useSignal(true);
  const errors = useSignal<{ email?: string | undefined; password?: string | undefined }>({});
  const error = useSignal<string | undefined>(undefined);
  const busy = useSignal(false);

  // Al ingelogd? Dan door naar vandaag.
  useEffect(() => {
    if (authState.value === 'unknown') refreshContext().catch(() => undefined);
  }, []);
  useEffect(() => {
    if (authState.value === 'authenticated') route(loginDestination(), true);
  }, [authState.value, route]);

  const submit = async () => {
    const v = validateLogin({ email: email.value, password: password.value });
    errors.value = {
      email: v.email ? m.web[v.email] : undefined,
      password: v.password ? m.web[v.password] : undefined,
    };
    if (v.email || v.password) return;
    busy.value = true;
    error.value = undefined;
    try {
      const res = await api.app.auth.login({
        email: normalizeEmail(email.value),
        password: password.value,
        remember_me: shared.value ? false : remember.value,
        shared_computer: shared.value,
      });
      sharedComputer.value = shared.value;
      loginHint.value = null;
      if (res.status === 'ok') {
        setAuthenticated(res.context);
        route(loginDestination(), true);
      } else {
        setPendingChallenge(res);
        route('/login/code');
      }
    } catch (e) {
      error.value = errorMessage(e, m);
    } finally {
      busy.value = false;
    }
  };

  return (
    <AuthLayout title={m.web.loginTitle}>
      {hint ? (
        <Notice kind="info" name="login-new-password">
          {appText.value.samen.invite.loginNew}
        </Notice>
      ) : null}
      {query.reason === 'untrusted_context' ? (
        <Notice kind="warning" name="reauth-notice">
          {m.web.outsideTrustedNetwork}
        </Notice>
      ) : null}
      {query.verified === '1' ? <Notice kind="success">{m.web.verifyDone}</Notice> : null}
      {query.reset === '1' ? <Notice kind="success">{m.web.resetDone}</Notice> : null}
      {error.value ? <Notice kind="error">{error.value}</Notice> : null}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <TextField
          id="email"
          label={m.web.email}
          type="email"
          value={email.value}
          onInput={(v) => (email.value = v)}
          autoComplete="username"
          inputMode="email"
          error={errors.value.email}
          required
        />
        <TextField
          id="password"
          label={m.web.password}
          type="password"
          value={password.value}
          onInput={(v) => (password.value = v)}
          autoComplete="current-password"
          error={errors.value.password}
          required
        />
        <Checkbox
          id="shared-computer"
          label={m.web.sharedComputer}
          checked={shared.value}
          onChange={(v) => (shared.value = v)}
          hint={shared.value ? m.web.sharedComputerHint : undefined}
        />
        <Checkbox
          id="remember-me"
          label={m.web.stayLoggedIn}
          checked={shared.value ? false : remember.value}
          onChange={(v) => (remember.value = v)}
          disabled={shared.value}
        />
        <Button name="login" type="submit" variant="primary" block busy={busy.value}>
          {m.web.login}
        </Button>
        <p class="auth-links">
          <a href="/reset">{m.web.forgotPassword}</a>
          <span> · </span>
          <a href="/register">{m.web.register}</a>
        </p>
      </form>
    </AuthLayout>
  );
}
